import { useRef } from 'react';
import { TrashIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { PencilSquareIcon } from '@heroicons/react/24/solid';

const ConsignmentSection = ({
  register,
  watch,
  getValues,
  setValue,
  setExpandedClients,
  setManifestFiles,
  onAddConsignment,
  downloadManifest,
  expandedClients,
  toggleClient,
  onDeleteConsignment,
  onEditConsignment,
  uploadClientManifest,
  onUploadConsignmentDocs,
  onDownloadConsignmentDoc,
  onDeleteConsignmentDoc,
  tripSaved,
  errors,
}) => {
  const manifestInputRefs = useRef({});

  const triggerManifestInput = (clientId) => {
    const inputs = manifestInputRefs.current[clientId];
    if (!inputs) return;
    const isMobile = window.innerWidth < 768;
    if (isMobile) inputs.mobile?.click();
    else inputs.desktop?.click();
  };

  const handleManifestChange = (clientId) => (e) => {
    const file = e.target.files[0];
    if (file) {
      setManifestFiles((prev) => ({ ...prev, [clientId]: file }));
      uploadClientManifest(clientId, file);
    }
    e.target.value = '';
  };

  const docInputRefs = useRef({});

  // Per-rowKey: { desktop: <input>, mobile: <input> }
  const triggerDocInput = (rowKey, isMobile) => {
    const inputs = docInputRefs.current[rowKey];
    if (!inputs) return;
    if (isMobile) inputs.mobile?.click();
    else inputs.desktop?.click();
  };

  const DocCell = ({ item, rowKey, docs, isSaved }) => (
    <div className="flex flex-col gap-1">
      {docs.map((doc) => (
        <div key={doc.id} className="flex items-center gap-1 text-xs">
          <PaperClipIcon className="h-3 w-3 text-gray-400 shrink-0" />
          <span
            className="truncate max-w-[120px] text-blue-600 cursor-pointer hover:underline"
            title={doc.original_filename}
            onClick={() => onDownloadConsignmentDoc(doc.id, doc.original_filename)}
          >
            {doc.original_filename}
          </span>
          <button
            type="button"
            title="Delete document"
            onClick={() => onDeleteConsignmentDoc(doc.id)}
            className="text-red-400 hover:text-red-600 shrink-0"
          >
            ×
          </button>
        </div>
      ))}

      {isSaved && tripSaved && (
        <>
          {/* Desktop: full file types */}
          <input
            type="file"
            accept="application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*"
            multiple
            className="hidden"
            ref={(el) => {
              if (!docInputRefs.current[rowKey]) docInputRefs.current[rowKey] = {};
              docInputRefs.current[rowKey].desktop = el;
            }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length) onUploadConsignmentDocs(item.id, files);
              e.target.value = '';
            }}
          />
          {/* Mobile: camera only */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            ref={(el) => {
              if (!docInputRefs.current[rowKey]) docInputRefs.current[rowKey] = {};
              docInputRefs.current[rowKey].mobile = el;
            }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length) onUploadConsignmentDocs(item.id, files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            title="Attach files"
            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1"
            onClick={() => triggerDocInput(rowKey, window.innerWidth < 768)}
          >
            <ArrowUpTrayIcon className="h-3 w-3" />
            Attach
          </button>
        </>
      )}

      {(!isSaved || !tripSaved) && docs.length === 0 && (
        <span className="text-xs text-gray-400 italic">Save trip to attach docs</span>
      )}
    </div>
  );

  return (
    <section>
      <div className="rounded p-3 uppercase">
        <div className="flex justify-center mb-4">
          <button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-full shadow"
            onClick={(e) => onAddConsignment(e)}
          >
            + ADD CONSIGNMENT
          </button>
        </div>

        {watch('consignment_groups')?.map((group, index) => {
          const clientId = group.client;
          const clientName = group.client_name;
          const manifest = group.manifest;
          const consignments = group.consignments || [];

          return (
            <div key={index} className="mt-3 border rounded bg-gray-50 p-2">
              {/* Group header */}
              <div className="flex justify-between items-center bg-gray-100 px-2 py-1 rounded">
                <div className="font-semibold text-sm">{clientName}</div>
                <div className="flex items-center gap-3">
                  {/* Delete group */}
                  <button
                    type="button"
                    onClick={() => {
                      const remaining = getValues("consignment_groups").filter(
                        (c) => c.client !== clientId
                      );
                      setValue("consignment_groups", remaining, { shouldDirty: true });
                      setExpandedClients((prev) => ({ ...prev, [clientId]: false }));
                      setManifestFiles((prev) => {
                        const next = { ...prev };
                        delete next[clientId];
                        return next;
                      });
                    }}
                    className="text-red-600"
                    title="Delete Group"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>

                  {/* Upload manifest */}
                  <div className="relative">
                    {/* Desktop: full file types */}
                    <input
                      type="file"
                      accept="application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*"
                      className="hidden"
                      ref={(el) => {
                        if (!manifestInputRefs.current[clientId]) manifestInputRefs.current[clientId] = {};
                        manifestInputRefs.current[clientId].desktop = el;
                      }}
                      onChange={handleManifestChange(clientId)}
                    />
                    {/* Mobile: camera only */}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      ref={(el) => {
                        if (!manifestInputRefs.current[clientId]) manifestInputRefs.current[clientId] = {};
                        manifestInputRefs.current[clientId].mobile = el;
                      }}
                      onChange={handleManifestChange(clientId)}
                    />
                    <button
                      type="button"
                      className="text-blue-600"
                      title="Upload Manifest"
                      onClick={() => triggerManifestInput(clientId)}
                    >
                      <ArrowUpTrayIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Download manifest */}
                  {manifest && (
                    <button
                      type="button"
                      onClick={() => downloadManifest(clientId, manifest)}
                      className="text-green-600"
                      title="Download Manifest"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                  )}

                  {/* Expand/Collapse */}
                  <div
                    onClick={() => toggleClient(clientId)}
                    className="cursor-pointer text-blue-600 text-sm select-none"
                  >
                    {expandedClients[clientId] ? '▼' : '▶'}
                  </div>
                </div>
              </div>

              {/* Consignment rows */}
              {expandedClients[clientId] && (
                <div className="mt-2">
                  {/* ── Desktop table ── */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left p-2 border">HWB/INVOICE NUMBER</th>
                          <th className="text-left p-2 border">TMS ID</th>
                          <th className="text-left p-2 border">Boxes</th>
                          <th className="text-left p-2 border">Weight</th>
                          <th className="text-left p-2 border">Remarks</th>
                          <th className="text-left p-2 border">Docs</th>
                          <th className="text-left p-2 border">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consignments.map((item, i) => {
                          const rowKey = item.id > 0 ? `c-${item.id}` : `t-${item.tmp_id}`;
                          const docs = item.documents || [];
                          const isSaved = item.id > 0;
                          return (
                            <tr key={i}>
                              <td className="p-2 border">{item.hwb_number}</td>
                              <td className="p-2 border">{item.tms_id}</td>
                              <td className="p-2 border">{item.total_boxes}</td>
                              <td className="p-2 border">{item.total_weight}</td>
                              <td className="p-2 border">{item.remarks}</td>
                              <td className="p-2 border">
                                <DocCell item={item} rowKey={rowKey} docs={docs} isSaved={isSaved} />
                              </td>
                              <td className="p-2 border">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => onEditConsignment(e, clientId, item)}
                                    className="p-1 rounded-full hover:bg-gray-100"
                                    title="Edit"
                                  >
                                    <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                                  </button>
                                  <button
                                    onClick={(e) => onDeleteConsignment(e, clientId, item)}
                                    className="p-1 rounded-full hover:bg-gray-100"
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-5 w-5 text-red-700" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Mobile cards ── */}
                  <div className="md:hidden space-y-3 mt-1">
                    {consignments.map((item, i) => {
                      const rowKey = item.id > 0 ? `c-${item.id}` : `t-${item.tmp_id}`;
                      const docs = item.documents || [];
                      const isSaved = item.id > 0;
                      return (
                        <div key={i} className="bg-white border rounded p-3 text-sm shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-gray-800">{item.hwb_number}</p>
                              {item.tms_id && (
                                <p className="text-xs text-gray-500">TMS: {item.tms_id}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={(e) => onEditConsignment(e, clientId, item)}
                                className="p-1 rounded-full hover:bg-gray-100"
                                title="Edit"
                              >
                                <PencilSquareIcon className="h-5 w-5 text-blue-600" />
                              </button>
                              <button
                                onClick={(e) => onDeleteConsignment(e, clientId, item)}
                                className="p-1 rounded-full hover:bg-gray-100"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5 text-red-700" />
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mb-3">
                            <div><span className="font-medium">Boxes:</span> {item.total_boxes}</div>
                            <div><span className="font-medium">Weight:</span> {item.total_weight} kg</div>
                            {item.remarks && (
                              <div className="col-span-2">
                                <span className="font-medium">Remarks:</span> {item.remarks}
                              </div>
                            )}
                          </div>

                          <div className="border-t pt-2">
                            <p className="text-xs font-medium text-gray-500 mb-1">Documents</p>
                            <DocCell item={item} rowKey={rowKey} docs={docs} isSaved={isSaved} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ConsignmentSection;
